import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import DataMixin from 'utils/stores/DataMixin';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { getErrorMessage, isNotFound } from 'utils/api/errors';

import {
  CreateScheduleData,
  ScheduleDetailsResponse,
  UpdateScheduleData,
} from 'utils/api/modules/schedules/schedules.types';

import RootStore from '../Root';

export default class ScheduleDetailsStore extends DataMixin {
  @observable error?: AxiosError;

  @observable details?: ScheduleDetailsResponse;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(scheduleId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.schedules.loadScheduleDetails(scheduleId)();
      runInAction(() => {
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load schedule details API response. Schedule id: ${scheduleId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action
  async createSchedule(data: CreateScheduleData): Promise<ScheduleDetailsResponse | null> {
    try {
      const { data: response } = await this.api.schedules.createSchedule(data)();
      this.store.notification.enqueueSuccessSnackbar('Schedule created successfully');
      return response;
    } catch (ex) {
      Logger.error('Invalid create schedule API response.', ex);
      this.store.notification.enqueueErrorSnackbar('Schedule could not be created');
      return null;
    }
  }

  @action
  async updateSchedule(scheduleId: string, data: UpdateScheduleData): Promise<boolean> {
    try {
      const { data: response } = await this.api.schedules.updateSchedule(scheduleId, data)();
      runInAction(() => {
        this.details = response;
      });
      this.store.notification.enqueueSuccessSnackbar('Schedule updated successfully');
      return true;
    } catch (ex) {
      const error = ex as AxiosError;
      if ((error.response?.status ?? 0) >= 500) Logger.error('Invalid update schedule API response.', ex);
      this.store.notification.enqueueErrorSnackbar(getErrorMessage(error, 'Schedule could not be updated'));
      return false;
    }
  }

  @action
  async deleteSchedule(scheduleId: string): Promise<boolean> {
    try {
      await this.api.schedules.deleteSchedule(scheduleId)();
      return true;
    } catch (ex) {
      Logger.error('Invalid delete schedule API response.', ex);
      this.store.notification.enqueueErrorSnackbar('Schedule could not be deleted');
      return false;
    }
  }

  @action
  async pauseSchedule(scheduleId: string): Promise<boolean> {
    try {
      const { data: response } = await this.api.schedules.pauseSchedule(scheduleId)();
      runInAction(() => {
        this.details = response;
      });
      this.store.notification.enqueueSuccessSnackbar('Schedule paused');
      return true;
    } catch (ex) {
      const error = ex as AxiosError;
      if ((error.response?.status ?? 0) >= 500) Logger.error('Schedule could not be paused', ex);
      this.store.notification.enqueueErrorSnackbar(getErrorMessage(error, 'Schedule could not be paused'));
      return false;
    }
  }

  @action
  async resumeSchedule(scheduleId: string): Promise<boolean> {
    try {
      const { data: response } = await this.api.schedules.resumeSchedule(scheduleId)();
      runInAction(() => {
        this.details = response;
      });
      this.store.notification.enqueueSuccessSnackbar('Schedule resumed');
      return true;
    } catch (ex) {
      const error = ex as AxiosError;
      if ((error.response?.status ?? 0) >= 500) Logger.error('Schedule could not be resumed', ex);
      this.store.notification.enqueueErrorSnackbar(getErrorMessage(error, 'Schedule could not be resumed'));
      return false;
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
  }
}
